import { createColumnHelper } from '@tanstack/react-table'
import { DevicePolicy } from '../../types/SettingsType'
import {
  IconAlertTriangle,
  IconBrandAndroid,
  IconBrandApple,
  IconCopy,
  IconDotsVertical,
  IconEdit,
  IconFaceId,
  IconLineScan,
  IconLockAccess,
  IconMapPin,
  IconMessageCancel,
  IconShieldDown,
  IconTrash,
} from '@tabler/icons-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Tooltip } from '@radix-ui/themes'

interface IPolicyColumnsProps {
  goToDevicePolicy: (policyId: number) => void
  renamePolicy: (policyId: number) => void
  deletePolicy: (policyId: number) => void
  duplicatePolicy: (policyId: number) => void
  isThreatLevelEnabled: boolean
}

export const policyColumns = (props: IPolicyColumnsProps) => {
  const { goToDevicePolicy, renamePolicy, deletePolicy, duplicatePolicy, isThreatLevelEnabled } = props
  const columnHelper = createColumnHelper<DevicePolicy>()

  const buildRuleIcons = (devicePolicy: DevicePolicy) => {
    const isDeviceIntegrityEnabled =
      devicePolicy.deviceIntegrityPeriod && devicePolicy.deviceIntegrityPeriod !== 'NEVER'
    const isScreenLockEnabled = devicePolicy.screenLockEnabled === 'true'
    const isSmishingEnabled = devicePolicy.smishingEnabled === 'true'
    const isBiometricsEnabled = devicePolicy.biometricsEnabled === 'true'
    const isAlertSeverityEnabled = devicePolicy.alertSeverity !== 'NONE'
    const isTravelEnabled = devicePolicy.travelEnabled === 'true'

    const isAndroidVersionEnabled = devicePolicy.androidMinimumVersion
    const isAndroidSecurityPatchEnabled = devicePolicy.androidLastPatchVersionGracePeriod

    const isIOSNMinusEnabled = devicePolicy.iosMinimumVersion || devicePolicy.iosVersionOffset

    return (
      <div className="flex gap-2">
        <Tooltip content="Android Outdated OS">
          <IconBrandAndroid key="outdatedOS" className={isAndroidVersionEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>

        <Tooltip content="Android Security Patch">
          <IconShieldDown
            key="outdatedOS"
            className={isAndroidSecurityPatchEnabled ? 'text-mainText' : 'text-neutral-8'}
          />
        </Tooltip>

        <Tooltip content="iOS Outdated OS">
          <IconBrandApple key="outdatedOS" className={isIOSNMinusEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>

        <Tooltip content="Biometrics">
          <IconFaceId key="biometrics" className={isBiometricsEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>

        <Tooltip content="Smishing">
          <IconMessageCancel key="smishing" className={isSmishingEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>

        <Tooltip content="Screen Lock">
          <IconLockAccess key="screenLock" className={isScreenLockEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>

        <Tooltip content="Device Check In">
          <IconLineScan
            key="deviceIntegrity"
            className={isDeviceIntegrityEnabled ? 'text-mainText' : 'text-neutral-8'}
          />
        </Tooltip>

        <Tooltip content="Alert Severity">
          <IconAlertTriangle
            key="alertSeverity"
            className={isAlertSeverityEnabled ? 'text-mainText' : 'text-neutral-8'}
          />
        </Tooltip>

        <Tooltip content="Location Sharing">
          <IconMapPin key="travel" className={isTravelEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>
      </div>
    )
  }

  const renderEnums = (enumValue: string) => {
    if (enumValue === '' || enumValue === null || enumValue === undefined || enumValue.toLowerCase() === 'none') {
      return '--'
    }
    return enumValue.toLowerCase()
  }

  const renderActionEnums = (enumValue: string | undefined) => {
    if (enumValue === '' || enumValue === null || enumValue === undefined || enumValue.toLowerCase() === 'none') {
      return '--'
    }
    return enumValue
  }

  const renderActive = (active: boolean) => {
    return (
      <div className="h-4 py-[3px] inline-flex justify-start items-center gap-2">
        {active ? (
          <div className="w-2 h-2 bg-successGreen rounded-full"></div>
        ) : (
          <div className="w-2 h-2 bg-[#737373] rounded-full"></div>
        )}
        <div className="justify-start text-text-Neutral-Secondary text-sm">{active ? 'Active' : 'Inactive'}</div>
      </div>
    )
  }

  const renderAssigned = (groupCount: number | undefined) => {
    if (groupCount === undefined || groupCount === 0) {
      return 'All members'
    }
    return `${groupCount} ${groupCount === 1 ? 'group' : 'groups'}`
  }

  const columnHelpers = [
    columnHelper.accessor('name', {
      header: 'Name',
    }),
    columnHelper.accessor('active', {
      header: 'Status',
      cell: (row: { row: { original: DevicePolicy } }) => renderActive(row.row.original.active),
    }),
    columnHelper.accessor('actions', {
      header: 'Actions',
      cell: (row: { row: { original: DevicePolicy } }) => (
        <p className="capitalize">{renderActionEnums(row.row.original.actions)}</p>
      ),
    }),
    columnHelper.accessor('groupCount', {
      header: 'Assigned',
      cell: (row: { row: { original: DevicePolicy } }) => <p>{renderAssigned(row.row.original.groupCount)}</p>,
    }),
    columnHelper.accessor('alertSeverity', {
      header: 'Rules',
      cell: (row: { row: { original: DevicePolicy } }) => buildRuleIcons(row.row.original),
    }),
    {
      id: 'button',
      cell: (row: { row: { original: DevicePolicy } }) => (
        <div className="h-[26px] flex float-right">
          <button
            className="h-[26px] cursor-pointer border border-mainBorder text-textTertiary rounded px-3 font-medium mr-2"
            onClick={() => {
              goToDevicePolicy(row.row.original.policyId!)
            }}
          >
            Edit Policy
          </button>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className="h-[26px] cursor-pointer border border-mainBorder text-textTertiary rounded px-2 font-medium">
                <IconDotsVertical size={16} />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="bg-white w-[120px] rounded-md border border-borderGrey dark:border-gray-300 p-2 shadow-lg -mr-5"
                align="end"
                side="bottom"
                sideOffset={6}
              >
                <DropdownMenu.Item
                  className="flex text-secondaryText text-sm cursor-pointer outline-none py-1 ml-1"
                  onClick={() => duplicatePolicy(row.row.original.policyId!)}
                >
                  <IconCopy size={16} className="mt-[5px] mr-2" />
                  <span className="mt-0.5">Duplicate</span>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className="flex text-secondaryText text-sm cursor-pointer outline-none py-1 ml-1"
                  onClick={() => renamePolicy(row.row.original.policyId!)}
                >
                  <IconEdit size={16} className="mt-[5px] mr-2" />
                  <span className="mt-0.5">Rename</span>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className="flex text-secondaryText text-sm cursor-pointer outline-none py-1 ml-1"
                  onClick={() => deletePolicy(row.row.original.policyId!)}
                >
                  <IconTrash size={16} className="mt-[5px] mr-2" />
                  <span className="mt-0.5">Delete</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      ),
    },
  ]

  if (isThreatLevelEnabled) {
    const threatLevelColumn = columnHelper.accessor('threatLevel', {
      header: 'Threat Level',
      cell: (row: { row: { original: DevicePolicy } }) => (
        <p className="capitalize">{renderEnums(row.row.original.threatLevel)}</p>
      ),
    })
    columnHelpers.splice(3, 0, threatLevelColumn)
  }

  return columnHelpers
}
